import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Button,
  Paper,
  Tooltip,
} from '@mui/material';
import { SurveyInstanceDTO } from '../dtos';
import { formatInstanceTimestamp, formatAbsoluteTimestamp } from '../../../shared/utils';

interface InstanceListProps {
  instances: SurveyInstanceDTO[];
  onSelectInstance: (instance: SurveyInstanceDTO) => void;
  selectedInstanceId?: string;
}

export const InstanceList: React.FC<InstanceListProps> = ({
  instances,
  onSelectInstance,
  selectedInstanceId,
}) => {
  if (instances.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Instances Yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create your first survey instance to get started.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
        Recent Instances - Last 12 Hours ({instances.length})
      </Typography>

      <List dense disablePadding>
        {instances.map((instance) => (
          <ListItem
            key={instance.id}
            button
            onClick={() => onSelectInstance(instance)}
            selected={selectedInstanceId === instance.id}
            sx={{
              py: 0.5,
              px: 1,
              minHeight: 'auto',
              '&.Mui-selected': {
                bgcolor: 'transparent',
                borderLeft: '3px solid',
                borderLeftColor: 'secondary.main',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              },
            }}
          >
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title={formatAbsoluteTimestamp(instance.createdAt)} arrow>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {formatInstanceTimestamp(instance.createdAt)}
                    </Typography>
                  </Tooltip>
                  <Chip
                    label="Active"
                    color="success"
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 16,
                      fontSize: '0.65rem',
                      '& .MuiChip-label': { px: 0.5 },
                    }}
                  />
                </Box>
              }
            />
            <ListItemSecondaryAction>
              <Button
                size="small"
                variant="text"
                color="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectInstance(instance);
                }}
                disabled={false}
                sx={{
                  minWidth: 'auto',
                  px: 1,
                  fontSize: '0.7rem',
                  textTransform: 'none',
                }}
              >
                Use
              </Button>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};
